import { VideoUpload } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Grid, Stack, Typography, SxProps, Theme, Box } from '@mui/material';

import { ProfileDocumentSection } from '../atoms/ProfileDocumentSection';
import { PublicProfileSection } from '../atoms/PublicProfileSection';
import { SnapshotEmployerSection } from '../atoms/SnapshotEmployerSection';
import { SnapshotEnrollmentSection } from '../atoms/SnapshotEnrollmentSection';
import { useStudentDetails } from '../hooks/useStudentDetails';

const PUBLIC_PROFILE_STRINGS = appStrings.modules.profile.public;

type PublicProfileSnapshotProps = Readonly<{
  publicProfile?: UserProfileResponse;
}>;

export const PublicProfileSnapshot = ({ publicProfile }: PublicProfileSnapshotProps) => {
  const {
    isStudent,
    allValues: findAllStringValues,
    profileValueOptions,
  } = useStudentDetails(publicProfile);

  const hasPurposeVideo = publicProfile?.profile?.documentsPurposeVideo?.[0]?.uri;

  return (
    <Box sx={containerSx}>
      <Typography variant="display-sm-medium">{PUBLIC_PROFILE_STRINGS.snapshot}</Typography>

      <Grid container spacing={4} sx={gridContainerSx}>
        {/**
         * COLUMN ONE - Current Enrollment, Sponsoring Institution, Organizations, Resume
         */}
        <Grid item xs={12} md={hasPurposeVideo ? 3 : 4} sx={gridItemSx}>
          <Stack spacing={3} sx={stackSx}>
            {isStudent && (
              <SnapshotEnrollmentSection
                publicProfile={publicProfile}
                profileValueOptions={profileValueOptions}
                findAllStringValues={findAllStringValues}
              />
            )}

            {/**
             * IF NOT A STUDENT, GENERATE DYNAMIC EMPLOYER SECTION
             */}
            {!isStudent && <SnapshotEmployerSection publicProfile={publicProfile} />}

            <PublicProfileSection
              title={PUBLIC_PROFILE_STRINGS.organizations}
              variant={{
                type: 'CHIP',
                chipType: 'tag',
                borderColor: 'light',
                values: findAllStringValues('resourcesClubs'),
              }}
            />

            <ProfileDocumentSection
              id="public-profile-resume"
              title={PUBLIC_PROFILE_STRINGS.resume}
              document={publicProfile?.profile?.documentsResumes?.[0]}
            />
          </Stack>
        </Grid>

        {/**
         * COLUMN TWO - Interests, Relationships, Languages, Personality Types
         */}
        <Grid item xs={12} md={hasPurposeVideo ? 3 : 8} sx={gridItemSx}>
          <Stack spacing={3} sx={stackSx}>
            <PublicProfileSection
              title={PUBLIC_PROFILE_STRINGS.interests}
              variant={{
                type: 'CHIP',
                chipType: 'pill',
                borderColor: 'dark',
                values: [
                  ...findAllStringValues('hobbiesCreativeInterests'),
                  ...findAllStringValues('hobbiesPassions'),
                  ...findAllStringValues('hobbiesFavorites'),
                ],
                showMoreConfig: {
                  minPerCategory: 3,
                  categories: ['hobbiesCreativeInterests', 'hobbiesPassions', 'hobbiesFavorites'],
                },
              }}
            />

            <PublicProfileSection
              title={PUBLIC_PROFILE_STRINGS.relationships}
              variant={{
                type: 'ICON_LIST',
                iconName: 'Colors',
                values: [
                  ...findAllStringValues('demographicsFamilyRoles'),
                  ...findAllStringValues('demographicsChildhoodExperiences'),
                ],
              }}
            />

            <PublicProfileSection
              title={PUBLIC_PROFILE_STRINGS.languages}
              variant={{
                type: 'ICON_LIST',
                iconName: 'Translate01',
                values: findAllStringValues('resourcesLanguages'),
              }}
            />

            <PublicProfileSection
              title={PUBLIC_PROFILE_STRINGS.personalityTypes}
              variant={{
                type: 'ICON_LIST',
                iconName: 'CpuChip01',
                values: [
                  ...findAllStringValues('demographicsMyersBriggs'),
                  ...findAllStringValues('demographicsAstrologySigns'),
                  ...findAllStringValues('demographicsStrengthsFinders'),
                  ...findAllStringValues('demographicsHigh5s'),
                ],
              }}
            />
          </Stack>
        </Grid>

        {/**
         * COLUMN THREE - Intro Video
         */}
        {hasPurposeVideo && (
          <Grid item xs={12} md={6} sx={gridVideoItemSx}>
            <Typography
              variant="text-sm-medium"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {PUBLIC_PROFILE_STRINGS.introVideo}
            </Typography>

            <VideoUpload
              mode="view"
              videoUrl={publicProfile?.profile?.documentsPurposeVideo?.[0]?.uri || ''}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  marginX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingTop: backrs.spacing['spacing-3xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,

  maxWidth: '1216px',
});

const gridContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: {
    xs: backrs.spacing['spacing-none'],
    md: backrs.spacing['spacing-3xl'],
  },
});

const gridItemSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: { md: `${backrs.spacing['spacing-none']} !important` },
});

const gridVideoItemSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: { md: `${backrs.spacing['spacing-none']} !important` },
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-md'],
});

const stackSx: SxProps<Theme> = {
  flexDirection: 'column',
  flexWrap: {
    xs: 'wrap',
    md: 'unset',
  },
};
